import React, { memo, useEffect, useMemo, useState } from "react";
import "./Product_filter.css";
import Slider from "@mui/material/Slider";
import {
  Brandings,
  getAllCategories,
  getAllColor,
  getAllFrameType,
  getAllGender,
  getAllMaterial,
  getAllShape,
} from "../../../features/master/masterSlice";
import { useDispatch, useSelector } from "react-redux";
import { GET } from "../../../utils/api";
import { API } from "../../../endpoints/common_endpoints";

const CONTACT_LENS_ID = 3;
const VISIBLE_LIMIT = 6; 


const VISION_FALLBACK = [
  { id: "distance", name: "Distance only" },
  { id: "multifocal", name: "Distance & Near (Multifocal)" },
];

/* ----------------  (only what is actually available) ---------------- */

const facetsCache = new Map();

const unwrapFacets = (res) =>
  [res, res?.data, res?.data?.data].find(
    (c) => c && Array.isArray(c.brands),
  ) || null;

const loadFacets = (categoryId) => {
  const key = String(categoryId || "");
  if (facetsCache.has(key)) return facetsCache.get(key);

  const qs = new URLSearchParams();
  if (categoryId) qs.set("category_id", categoryId);
  qs.set("customer_view", "true");

  const promise = Promise.resolve(GET(`${API.GET_MENU_FILTERS}?${qs}`))
    .then((res) => {
      const payload = unwrapFacets(res);
      if (!payload) facetsCache.delete(key);
      return payload; 
    })
    .catch(() => {
      facetsCache.delete(key);
      return null;
    });

  facetsCache.set(key, promise);
  return promise;
};

/* ---------------- price helpers ---------------- */

const fmt = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const buildBuckets = (lo, hi) => {
  if (!hi || hi <= lo) return [];
  const candidates = [250, 500, 1000, 2000, 3000, 5000, 10000, 20000, 50000];
  let edges = candidates.filter((e) => e > lo && e < hi);
  if (edges.length > 6) {
    const step = Math.ceil(edges.length / 6);
    edges = edges.filter((_, i) => i % step === 0);
  }
  if (edges.length === 0) return [{ label: "All prices", min: lo, max: hi }];

  const out = [{ label: `Under ${fmt(edges[0])}`, min: lo, max: edges[0] }];
  for (let i = 0; i < edges.length - 1; i++) {
    out.push({
      label: `${fmt(edges[i])} – ${fmt(edges[i + 1])}`,
      min: edges[i],
      max: edges[i + 1],
    });
  }
  out.push({
    label: `${fmt(edges[edges.length - 1])} & above`,
    min: edges[edges.length - 1],
    max: hi,
  });
  return out;
};

/* ---------------- small UI pieces ---------------- */

const FilterSection = ({ title, count = 0, open, onToggle, children }) => (
  <div className={`pf_section ${open ? "open" : ""}`}>
    <button
      type="button"
      className="pf_section_head"
      onClick={onToggle}
      aria-expanded={open}
    >
      <span className="pf_section_title">{title}</span>
      {count > 0 && <span className="pf_count">{count}</span>}
      <span className="pf_chev" />
    </button>
    {open && <div className="pf_section_body">{children}</div>}
  </div>
);

const OptionList = memo(({ list, selected, onToggle }) => {
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(false);

  const term = q.trim().toLowerCase();

  const shown = useMemo(
    () =>
      term
        ? list.filter((i) => (i?.name || "").toLowerCase().includes(term))
        : list,
    [list, term],
  );

  const isSel = (item) =>
    selected.some((s) => String(s.id) === String(item.id));

  
  const collapsed = !expanded && !term && shown.length > VISIBLE_LIMIT;
  const visible = collapsed
    ? shown.filter((item, i) => i < VISIBLE_LIMIT || isSel(item))
    : shown;
  const hiddenCount = shown.length - visible.length;

  if (list.length === 0) {
    return <p className="pf_empty">No options available</p>;
  }

  return (
    <>
      {list.length > 7 && (
        <input
          type="text"
          className="pf_search"
          placeholder="Search…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      )}

      <div className="pf_options">
        {visible.map((item) => {
          const checked = isSel(item);
          return (
            <label
              key={item.id}
              className={`pf_option ${checked ? "checked" : ""}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle({ id: item.id, name: item.name })}
              />
              <span className="pf_option_name">{item.name}</span>
            </label>
          );
        })}
        {shown.length === 0 && <p className="pf_empty">No match found</p>}
      </div>

      {!term && shown.length > VISIBLE_LIMIT && (
        <button
          type="button"
          className="pf_more"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Show less" : `Show ${hiddenCount} more`}
        </button>
      )}
    </>
  );
});

/* ---------------- main component ---------------- */

const Product_filter = ({
  handleFilterReset,
  onFilterChange,
  setPrice,
  price,
  filters,
}) => {
  const dispatch = useDispatch();

  const priceRange = useSelector((state) => state.master.price_range);
  const allCategories = useSelector((state) => state.master.categories);
  const allGender = useSelector((state) => state.master.all_gender);
  const allMaterial = useSelector((state) => state.master.all_material);
  const allFrameType = useSelector((state) => state.master.all_frame_type);
  const allColor = useSelector((state) => state.master.all_color);
  const allBrands = useSelector((state) => state.master.brands);
  const allShape = useSelector((state) => state.master.all_shape);

  const selectedOf = (key) =>
    Array.isArray(filters?.[key]) ? filters[key] : [];

  const categoryIds = useMemo(
    () => selectedOf("category").map((c) => c.id),
    // eslint-disable-next-line
    [filters?.category],
  );
  const catKey = categoryIds.join(",");

  const isContactLens =
    categoryIds.length === 1 && Number(categoryIds[0]) === CONTACT_LENS_ID;

  /* ----- master lists ----- */
  useEffect(() => {
    dispatch(getAllCategories());
    dispatch(getAllGender());
    dispatch(getAllFrameType());
    dispatch(getAllShape());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllMaterial({ category_id: categoryIds }));
    dispatch(Brandings({ category_id: categoryIds }));
    dispatch(getAllColor({ category_id: categoryIds }));
    // eslint-disable-next-line
  }, [dispatch, catKey]);

  /* -----  (customer view only) ----- */
  const [facets, setFacets] = useState(null);

  useEffect(() => {
    let alive = true;
    loadFacets(categoryIds.length === 1 ? categoryIds[0] : "").then((d) => {
       console.log("facets", d, Object.keys(d || {}));
      if (alive) setFacets(d);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line
  }, [catKey]);

  
  const options = (filterKey, facetKey, master, fallback = []) => {
    const base =
      facets && Array.isArray(facets[facetKey])
        ? facets[facetKey]
        : master?.length
          ? master
          : fallback;

    const extra = selectedOf(filterKey).filter(
      (s) => !base.some((b) => String(b.id) === String(s.id)),
    );
    return [...base, ...extra];
  };

  // Product names for contact lenses.

  const productOptions = () => {
    const raw =
      facets?.products || facets?.product || facets?.productNames || [];

    const brandIds = selectedOf("brands").map((b) => String(b.id));

    const base = raw
      .filter((p) => p && p.id != null && (p.name || p.product_name))
      .map((p) => ({
        id: p.id,
        name: p.name || p.product_name,
        brand_id: p.brand_id ?? p.brand?.id,
      }))
      .filter(
        (p) =>
          brandIds.length === 0 ||
          p.brand_id == null ||
          brandIds.includes(String(p.brand_id)),
      );

   
    const extra = selectedOf("product").filter(
      (s) => !base.some((b) => String(b.id) === String(s.id)),
    );
    return [...base, ...extra];
  };

  /* ----- open / close sections ----- */
  const [openKeys, setOpenKeys] = useState({ category: true });

  useEffect(() => {
    setOpenKeys(
      isContactLens
        ? { category: true, modality: true, brands: true }
        : { category: true, gender: true },
    );
  }, [isContactLens]);

  const toggleOpen = (k) => setOpenKeys((p) => ({ ...p, [k]: !p[k] }));

  /* ----- price ----- */
  
  const lo = Math.floor(
    Number(facets?.price_range?.lowest_price ?? priceRange?.lowest_price) || 0,
  );
  const hi = Math.ceil(
    Number(facets?.price_range?.highest_price ?? priceRange?.highest_price) ||
      0,
  );

  const [value, setValue] = useState([lo, hi]);
  const [minIn, setMinIn] = useState("");
  const [maxIn, setMaxIn] = useState("");

  useEffect(() => {
    const cur =
      Array.isArray(price) && price.length === 2 ? price : [lo, hi];
    setValue([Number(cur[0]), Number(cur[1])]);
    setMinIn("");
    setMaxIn("");
  }, [lo, hi, price?.[0], price?.[1]]); // eslint-disable-line

  const priceActive =
    Array.isArray(price) &&
    price.length === 2 &&
    (Number(price[0]) > lo || Number(price[1]) < hi);

  const buckets = useMemo(() => buildBuckets(lo, hi), [lo, hi]);

  const commitPrice = (a, b) => {
    const min = Math.max(lo, Math.min(a, b));
    const max = Math.min(hi, Math.max(a, b));
    setPrice?.([min, max]);
  };

  const applyCustom = () => {
    const a = minIn === "" ? lo : Number(minIn);
    const b = maxIn === "" ? hi : Number(maxIn);
    if (Number.isNaN(a) || Number.isNaN(b)) return;
    commitPrice(a, b);
  };

  const clearPrice = () => setPrice?.([lo, hi]);

  const renderPrice = () => (
    <>
      <div className="pf_buckets">
        {buckets.map((b) => {
          const active =
            priceActive &&
            Number(price[0]) === b.min &&
            Number(price[1]) === b.max;
          return (
            <button
              type="button"
              key={b.label}
              className={`pf_bucket ${active ? "active" : ""}`}
              onClick={() => (active ? clearPrice() : commitPrice(b.min, b.max))}
            >
              {b.label}
            </button>
          );
        })}
      </div>

      {hi > lo && (
        <div className="pf_slider">
          <Slider
            size="small"
            getAriaLabel={() => "Price range"}
            value={value}
            onChange={(e, v) => setValue(v)}
            onChangeCommitted={(e, v) => commitPrice(v[0], v[1])}
            valueLabelDisplay="off"
            min={lo}
            max={hi}
          />
          <div className="pf_slider_labels">
            <span>{fmt(value[0])}</span>
            <span>{fmt(value[1])}</span>
          </div>
        </div>
      )}

      <div className="pf_custom">
        <input
          type="number"
          min={0}
          placeholder={`₹ ${lo}`}
          value={minIn}
          onChange={(e) => setMinIn(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applyCustom()}
        />
        <span className="pf_dash">–</span>
        <input
          type="number"
          min={0}
          placeholder={`₹ ${hi}`}
          value={maxIn}
          onChange={(e) => setMaxIn(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applyCustom()}
        />
        <button type="button" className="pf_go" onClick={applyCustom}>
          Go
        </button>
      </div>
    </>
  );

  /* ----- which sections to show ----- */
  const sections = isContactLens
    ? [
        { key: "category", title: "Product Categories", list: options("category", null, allCategories) },
        { key: "modality", title: "Modality", list: options("modality", "modalities", []) },
        { key: "vision", title: "Vision", list: options("vision", "visions", [], VISION_FALLBACK) },
        { key: "brands", title: "Brand", list: options("brands", "brands", allBrands) },
        { key: "product", title: "Product", list: productOptions() },
        { key: "price", title: "Price" },
      ]
    : [
        { key: "category", title: "Product Categories", list: options("category", null, allCategories) }, // null = always the full master list, so other categories stay visible
        { key: "gender", title: "Gender", list: options("gender", "genders", allGender) },
        { key: "material", title: "Material", list: options("material", "materials", allMaterial) },
        { key: "frameType", title: "Frame Type", list: options("frameType", "frameTypes", allFrameType) },
        { key: "colour", title: "Colour", list: options("colour", "colours", allColor) },
        { key: "brands", title: "Brands", list: options("brands", "brands", allBrands) },
        { key: "frameShape", title: "Frame Shape", list: options("frameShape", "shapes", allShape) },
        { key: "price", title: "Price" },
      ];

  const activeCount =
    sections.reduce(
      (n, s) => (s.key === "price" ? n : n + selectedOf(s.key).length),
      0,
    ) + (priceActive ? 1 : 0);

  const handleClearAll = () => {
    handleFilterReset?.();
    clearPrice();
  };

  return (
    <section className="Product_filter">
      <div className="pf_top">
        <span className="pf_top_title">
          Filters
          {activeCount > 0 && <span className="pf_count">{activeCount}</span>}
        </span>
        {activeCount > 0 && (
          <button type="button" className="pf_clear" onClick={handleClearAll}>
            Clear all
          </button>
        )}
      </div>

      {sections.map((s) => (
        <FilterSection
          key={s.key}
          title={s.title}
          count={
            s.key === "price" ? (priceActive ? 1 : 0) : selectedOf(s.key).length
          }
          open={!!openKeys[s.key]}
          onToggle={() => toggleOpen(s.key)}
        >
          {s.key === "price" ? (
            renderPrice()
          ) : (
            <OptionList
              list={s.list}
              selected={selectedOf(s.key)}
              onToggle={(item) => onFilterChange(s.key, item)}
            />
          )}
        </FilterSection>
      ))}
    </section>
  );
};

export default Product_filter;